import React from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Radio,
  RadioGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import RadioButtonUncheckedOutlinedIcon from '@mui/icons-material/RadioButtonUncheckedOutlined';
import { brand } from '../../theme';
import { visuallyHidden } from '../Insights/visuallyHidden';
import { EVERY_PLAN_NOTE, formatPlanPrice, isFreePlan, planFacts, type BillingSummary } from './billingModel';
import type { SubscriptionPlan } from './billingTypes';
import type { BillingNotice } from './useBillingActions';

interface PlanPickerDialogProps {
  open: boolean;
  /** Sorted by price. */
  plans: SubscriptionPlan[];
  summary: BillingSummary;
  selectedPlanId: string | null;
  onSelect: (planId: string) => void;
  onClose: () => void;
  onContinue: () => void;
  continueLabel: string;
  busy: boolean;
  /** The provider is still loading: nothing can run yet. */
  waiting?: boolean;
  error?: BillingNotice | null;
  onDismissError?: () => void;
}

/** "Your plan", "Switches on Nov 15, 2026": a plan's relation to this account, in words. */
const planState = (plan: SubscriptionPlan, summary: BillingSummary): string | null => {
  const until = summary.periodEndLabel;
  if (plan.id === summary.planId) {
    if (summary.isLegacyPaidBasic) return 'Your plan (original paid Basic)';
    if ((summary.phase === 'ending' || summary.phase === 'changing') && until) return `Your plan until ${until}`;
    return 'Your plan';
  }
  if (plan.id === summary.nextPlanId) {
    return until ? `Switches on ${until}` : 'Switches at the end of your billing period';
  }
  return null;
};

/** One plan as a radio option: name, price, then the same rows as every other plan. */
const PlanOption: React.FC<{
  plan: SubscriptionPlan;
  summary: BillingSummary;
  selected: boolean;
}> = ({ plan, summary, selected }) => {
  const nameId = `plan-option-${plan.id}-name`;
  const detailsId = `plan-option-${plan.id}-details`;
  const state = planState(plan, summary);
  const free = isFreePlan(plan);

  return (
    <Box
      component="label"
      data-testid={`plan-option-${plan.id}`}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        cursor: 'pointer',
        borderRadius: 3,
        backgroundColor: 'background.paper',
        // 1px border, 2px copper when selected; the padding makes up the
        // difference so the content doesn't shift.
        border: selected ? `2px solid ${brand.copper}` : `1px solid ${brand.border}`,
        p: selected ? 'calc(20px - 1px)' : '20px',
        transition: 'border-color 0.15s ease',
        '&:hover': { borderColor: selected ? brand.copper : brand.borderStrong },
        '&:has(input:focus-visible)': { outline: `2px solid ${brand.copper}`, outlineOffset: 2 },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography id={nameId} component="span" sx={{ display: 'block', fontWeight: 700, fontSize: '1.0625rem', color: 'text.primary' }}>
            {plan.name}
          </Typography>
          {/* The line is kept on every card so the rows below line up across cards. */}
          <Typography
            component="span"
            variant="caption"
            aria-hidden={plan.popular ? undefined : true}
            sx={{ display: 'block', color: 'text.secondary', fontWeight: 600, minHeight: '1.66em' }}
          >
            {plan.popular ? 'Most popular' : ' '}
          </Typography>
        </Box>
        <Radio
          value={plan.id}
          checked={selected}
          icon={<RadioButtonUncheckedOutlinedIcon sx={{ fontSize: 22 }} />}
          checkedIcon={<CheckCircleOutlinedIcon sx={{ fontSize: 22 }} />}
          inputProps={{ 'aria-labelledby': nameId, 'aria-describedby': detailsId }}
          sx={{ p: 0.5, mt: -0.5, mr: -0.5 }}
        />
      </Box>

      <Box id={detailsId} sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
        <Typography sx={{ mt: 1.5, fontWeight: 700, color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
          {formatPlanPrice(plan, summary.currency)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {free ? 'No card needed' : 'Cancel any time'}
        </Typography>

        <Box component="dl" sx={{ m: 0, mt: 2, flex: 1 }}>
          {planFacts(plan.limits, plan.includes, plan.id).map((fact) => (
            <Box
              key={fact.label}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 2,
                py: 1,
                borderTop: `1px solid ${brand.border}`,
              }}
            >
              <Typography component="dt" variant="body2" color="text.secondary">
                {fact.label}
              </Typography>
              <Typography
                component="dd"
                variant="body2"
                sx={{
                  m: 0,
                  fontWeight: 600,
                  color: fact.missing ? 'text.secondary' : 'text.primary',
                  textAlign: 'right',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {fact.missing ? (
                  <>
                    <span aria-hidden="true">{fact.value}</span>
                    <Box component="span" sx={visuallyHidden}>
                      Not included
                    </Box>
                  </>
                ) : (
                  fact.value
                )}
              </Typography>
            </Box>
          ))}
        </Box>

        <Typography
          variant="body2"
          sx={{ mt: 1.5, minHeight: '1.6em', fontWeight: 600, color: state ? 'text.primary' : 'transparent' }}
          aria-hidden={state ? undefined : true}
        >
          {state || ' '}
        </Typography>
      </Box>
    </Box>
  );
};

/**
 * Choose a plan: a keyboard-accessible radio group (arrow keys move between
 * plans) with identical rows on every card. Continuing never charges by
 * itself; checkout, or a confirmation that states the charge, comes next.
 */
const PlanPickerDialog: React.FC<PlanPickerDialogProps> = ({
  open,
  plans,
  summary,
  selectedPlanId,
  onSelect,
  onClose,
  onContinue,
  continueLabel,
  busy,
  waiting = false,
  error,
  onDismissError,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isChange = summary.hasLiveSubscription || !summary.planIsFree;
  // With a switch scheduled, choosing the current plan keeps it (and cancels the switch).
  const keepsCurrent = selectedPlanId === summary.planId && summary.phase === 'changing';
  const canContinue = Boolean(selectedPlanId) && (selectedPlanId !== summary.planId || keepsCurrent) && !busy && !waiting;
  const currentPlanName = plans.find((plan) => plan.id === summary.planId)?.name || 'my plan';

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!busy) onClose();
      }}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
    >
      <DialogTitle sx={{ pb: 0.5 }}>{isChange ? 'Change plan' : 'Choose a plan'}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          {summary.hasLiveSubscription
            ? 'Upgrades start now and you pay the prorated difference today. Downgrades start at the end of your billing period.'
            : 'Paid plans are billed monthly, plus applicable tax. Cancel any time.'}
        </Typography>

        {error && (
          <Alert severity={error.severity} onClose={onDismissError} sx={{ mb: 2, whiteSpace: 'pre-line' }}>
            {error.message}
          </Alert>
        )}

        {plans.length === 0 ? (
          <Alert severity="warning">The plans couldn&apos;t be loaded. Close this and try again in a moment.</Alert>
        ) : (
          <>
            <RadioGroup
              aria-label="Plans"
              name="plan"
              value={selectedPlanId ?? ''}
              onChange={(event) => onSelect(event.target.value)}
            >
              <Grid container spacing={2}>
                {plans.map((plan) => (
                  <Grid item xs={12} md={4} key={plan.id}>
                    <PlanOption plan={plan} summary={summary} selected={plan.id === selectedPlanId} />
                  </Grid>
                ))}
              </Grid>
            </RadioGroup>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }} data-testid="plan-picker-every-plan">
              {EVERY_PLAN_NOTE}
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={busy} color="inherit">
          Cancel
        </Button>
        <Button variant="contained" onClick={onContinue} disabled={!canContinue}>
          {busy ? 'Working…' : keepsCurrent ? `Keep ${currentPlanName}` : continueLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlanPickerDialog;
